import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/cjs/styles/prism";

export default function ApiCodeBlock({
  method,
  endpoint = "/api/v1/endpoint?api_key=[your-api-key]&other_params=value",
  contentType,
  language = "bash", // default language
  code,
  xApiToken,
}: {
  method?: string;
  endpoint?: string | string[];
  contentType?: string | { type: string; body?: Record<string, any> };
  language?: string;
  code?: string;
  xApiToken?: string;
}) {
  // normalize endpoint into an array
  const lines = Array.isArray(endpoint)
    ? endpoint
    : (endpoint as string).split("\n");

  // if raw code is provided, render with syntax highlighting
  if (code) {
    return (
      <div className="max-w-4xl overflow-hidden rounded-xl bg-black">
        {/* Label */}
        <div className="gap-2.5 px-3 pt-3 pb-1 text-[10px] leading-[140%] text-neutral-50/60 uppercase">
          {language}
        </div>

        <SyntaxHighlighter
          language={language}
          style={atomDark}
          customStyle={{
            margin: 0,
            padding: "16px",
            background: "transparent",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    );
  }

  // otherwise fall back to API HTTP style block
  return (
    <div className="max-w-4xl rounded-xl bg-black">
      <div className="space-y-1">
        {/* Label */}
        <div className="gap-2.5 px-3 pt-3 pb-1 text-[10px] leading-[140%] text-neutral-50/60 uppercase">
          HTTP
        </div>

        {/* API example */}
        <pre className="px-3 pt-2 pb-3 font-mono text-sm leading-[140%] font-normal whitespace-pre-wrap text-neutral-50">
          {lines.map((line, idx) => {
            const trimmed = line.trim();

            if (trimmed === "") {
              return <div key={idx}>&nbsp;</div>;
            }

            // Render comment lines differently
            if (trimmed.startsWith("#")) {
              return (
                <div key={idx} className="text-neutral-400">
                  {trimmed}
                </div>
              );
            }

            // Render API call lines
            return (
              <div key={idx} className="flex flex-wrap gap-2">
                {method && <span>{method}</span>}
                <span>{trimmed}</span>
              </div>
            );
          })}

          {xApiToken && typeof xApiToken === "string" && (
            <div className="font-mono">
              <span className="text-neutral-50">X-API-Token: </span>
              <span>{xApiToken}</span>
            </div>
          )}

          {/* String content-type */}
          {contentType && typeof contentType === "string" && (
            <div className="font-mono">
              <span className="text-neutral-50">Content-Type: </span>
              <span>{contentType}</span>
            </div>
          )}

          {/* Object content-type */}
          {contentType &&
            typeof contentType === "object" &&
            "type" in contentType && (
              <div className="font-mono">
                <div className="text-[#FF4DFD]">
                  Content-Type:{" "}
                  <span className="text-neutral-50">{contentType.type}</span>
                </div>
                {contentType.body && (
                  <SyntaxHighlighter
                    language="json"
                    style={atomDark}
                    customStyle={{
                      background: "transparent",
                      fontSize: "14px",
                    }}
                  >
                    {JSON.stringify(contentType.body, null, 2)}
                  </SyntaxHighlighter>
                )}
              </div>
            )}
        </pre>
      </div>
    </div>
  );
}
